'use client'
import { ContainerCustom } from '@/components'
import { COMMON_SPACING_SECTION } from '@/constants'
import { TContentProductItem2, TContentProductResponse } from '@/features/products/types'
import { useWindowSize } from '@/hooks/use-window-size'
import { Box, BoxProps, Stack, Typography } from '@mui/material'
import HTMLReactParser from 'html-react-parser/lib/index'
import isEmpty from 'lodash/isEmpty'
import Image from 'next/image'
import { Swiper, SwiperSlide } from 'swiper/react'

export const SwiperFactory = ({
  content,
  hideTitle = false,
  boxWrapSlideProps,
  parentProps
}: {
  content: TContentProductResponse['data']['items']
  hideTitle?: boolean
  boxWrapSlideProps?: Omit<BoxProps, 'children'>
  parentProps?: Omit<BoxProps, 'children'>
}) => {
  const firstSection = content?.find((item) => item?.section_1)?.section_1
  const tempUnEmptyList = firstSection?.items?.filter((item) => !isEmpty(item)) || []
  const { width } = useWindowSize()
  const breakpointsMobile = width < 600
  return (
    <Box paddingTop={COMMON_SPACING_SECTION} {...parentProps}>
      {!hideTitle && (
        <Typography
          variant="h4"
          mb={{ xs: 2, mb: 4 }}
          fontWeight={600}
          sx={{ fontSize: { xs: '1.5rem', md: '2rem', lg: '2.5rem' } }}
          lineHeight={{ xs: '1.375', md: '1.5' }}
          // maxWidth={{ xs: '100%', md: '70%', lg: '50%' }}
        >
          {HTMLReactParser(firstSection?.title || '')}
        </Typography>
      )}
      <Box
        {...boxWrapSlideProps}
        sx={{
          width: tempUnEmptyList?.length === 3 ? '100%' : `calc(100% + (100vw - 100%)/2 - 6px)`,
          ...boxWrapSlideProps?.sx,
        }}
      >
        {breakpointsMobile ? (
          <Stack gap={2}>
            {tempUnEmptyList?.map((item, index) => {
              if (isEmpty(item)) return null
              return (
                <Box key={index}>
                  <ContentItem item={item} index={index} />
                </Box>
              )
            })}
          </Stack>
        ) : (
          <Swiper
            spaceBetween={16}
            breakpoints={{
              600: {
                slidesPerView: 1.6,
              },
              768: {
                slidesPerView: tempUnEmptyList?.length === 3 ? 3 : 2.6,
              },
            }}
          >
            {tempUnEmptyList?.map((item, index) => {
              if (isEmpty(item)) return null
              return (
                <SwiperSlide key={index} style={{ cursor: 'grab' }}>
                  <ContentItem item={item} index={index} />
                </SwiperSlide>
              )
            })}
          </Swiper>
        )}
      </Box>
    </Box>
  )
}

//#region ContentItem
export const ContentItem = ({ item, index }: { item: TContentProductItem2; index: number }) => {
  return (
    <Stack spacing={2} sx={{ maxWidth: { xs: '100vw', md: '60vw', xxl: '33vw' } }}>
      <Box
        sx={{
          width: '100%',
          height: 'auto',
          aspectRatio: '4 / 3',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <Image
          sizes="(max-width: 768px) 100vw, 50vw"
          src={item?.image_url || ''}
          alt={item?.title || `factory-image-${index}`}
          layout="responsive"
          width={640}
          height={480}
          loading="lazy"
          quality={90}
          style={{ borderRadius: '8px', objectFit: 'cover', width: '100%', height: '100%' }}
        />
      </Box>
      <Typography
        fontWeight={600}
        sx={{ fontSize: { xs: '1.15rem', md: '1.5rem' } }}
        lineHeight={{ xs: 1.375, md: 1.5 }}
      >
        {HTMLReactParser(item?.title || '')}
      </Typography>
    </Stack>
  )
}
